import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { api, type DeckSummary } from '../../lib/api';
import { navigate, paths } from '../../lib/router';
import { useDeckStore } from '../../store/deckStore';
import { ConfirmDialog } from '../ConfirmDialog';
import { CreateButtons } from './HomePage';
import { DeckCard, DeckGridSkeleton, EmptyState, PageHeader } from './portalParts';
import { deckGrid } from './portalStyles';

type Filter = 'all' | 'private' | 'company' | 'unlisted';

function visibilityBadge(visibility: DeckSummary['visibility']) {
  if (visibility === 'company') {
    return <Chip size="small" color="primary" label="Shared" sx={{ height: 20, fontSize: 11 }} />;
  }
  if (visibility === 'unlisted') {
    return <Chip size="small" label="Link only" sx={{ height: 20, fontSize: 11 }} />;
  }
  return undefined;
}

export function MyDecksPage() {
  const duplicateDeck = useDeckStore((s) => s.duplicateDeck);
  const deleteDeck = useDeckStore((s) => s.deleteDeck);
  const [decks, setDecks] = useState<DeckSummary[] | null>(null);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [menu, setMenu] = useState<{ anchor: HTMLElement; deck: DeckSummary } | null>(null);
  const [toDelete, setToDelete] = useState<DeckSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    () =>
      api
        .listDecks('mine')
        .then((r) => setDecks(r.decks))
        .catch((err) => setError(err instanceof Error ? err.message : 'Could not load your decks')),
    [],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (decks ?? []).filter(
      (d) => (filter === 'all' || d.visibility === filter) && (!q || d.title.toLowerCase().includes(q)),
    );
  }, [decks, query, filter]);

  return (
    <>
      <PageHeader title="My decks" description="Everything you've made. Shared decks also appear in their department folder." actions={<CreateButtons />} />
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 3 }}>
        <TextField
          placeholder="Search your decks"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ flex: 1, maxWidth: 420 }}
          slotProps={{
            htmlInput: { 'aria-label': 'Search your decks' },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18 }} />
                </InputAdornment>
              ),
            },
          }}
        />
        <ToggleButtonGroup exclusive value={filter} onChange={(_, v: Filter | null) => v && setFilter(v)} aria-label="Filter decks">
          <ToggleButton value="all" sx={{ px: 2 }}>
            All
          </ToggleButton>
          <ToggleButton value="private" sx={{ px: 2 }}>
            Private
          </ToggleButton>
          <ToggleButton value="company" sx={{ px: 2 }}>
            Shared
          </ToggleButton>
          <ToggleButton value="unlisted" sx={{ px: 2 }}>
            Link only
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {decks === null ? (
        <DeckGridSkeleton count={8} />
      ) : filtered.length ? (
        <Box sx={deckGrid}>
          {filtered.map((d) => (
            <DeckCard
              key={d.id}
              deck={d}
              onOpen={() => navigate(paths.edit(d.id))}
              badge={visibilityBadge(d.visibility)}
              menu={
                <IconButton size="small" aria-label={`Actions for ${d.title}`} onClick={(e) => setMenu({ anchor: e.currentTarget, deck: d })}>
                  <MoreHorizIcon fontSize="small" />
                </IconButton>
              }
            />
          ))}
        </Box>
      ) : (
        <EmptyState>{decks.length ? 'No decks match your search.' : "You haven't made a deck yet."}</EmptyState>
      )}

      <Menu
        anchorEl={menu?.anchor}
        open={Boolean(menu)}
        onClose={() => setMenu(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 180 } } }}
      >
        <MenuItem
          onClick={() => {
            const d = menu?.deck;
            setMenu(null);
            if (d) navigate(paths.view(d.id));
          }}
        >
          <ListItemIcon>
            <VisibilityOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Preview" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            const d = menu?.deck;
            setMenu(null);
            if (d) void duplicateDeck(d.id).then((id) => navigate(paths.edit(id)));
          }}
        >
          <ListItemIcon>
            <ContentCopyOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Duplicate" />
        </MenuItem>
        <MenuItem
          sx={{ color: 'error.main' }}
          onClick={() => {
            setToDelete(menu?.deck ?? null);
            setMenu(null);
          }}
        >
          <ListItemIcon sx={{ color: 'inherit' }}>
            <DeleteOutlineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Delete" />
        </MenuItem>
      </Menu>
      <ConfirmDialog
        open={Boolean(toDelete)}
        title="Delete deck?"
        message={`“${toDelete?.title ?? ''}” will be permanently deleted${toDelete?.visibility === 'company' ? ' and removed from its department folder' : ''}. This can't be undone.`}
        confirmLabel="Delete deck"
        destructive
        onClose={() => setToDelete(null)}
        onConfirm={() => {
          const d = toDelete;
          if (d)
            void deleteDeck(d.id)
              .then(load)
              .catch((err) => setError(err instanceof Error ? err.message : 'Could not delete the deck'));
        }}
      />
    </>
  );
}
